import { Component, inject, signal } from '@angular/core';
import { DialogErrorBar } from '../../core/dialogs/dialog-error-bar/dialog-error-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { form, FormField, required } from '@angular/forms/signals';
import { FormFieldWrapper } from '../../core/form/form-field-wrapper/form-field-wrapper';
import { AuthService } from '../services/auth.service';
import { NotificationService } from '../../core/services/notification-service/notification-service';
import { defer } from 'rxjs';
import { dialogSubmission } from '../../core/dialogs/services/dialog-mutation-service/dialog-submission';
import { passwordStrengthRules } from '../password-rules';

interface ChangePasswordModel {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

@Component({
  selector: 'app-change-password-dialog',
  imports: [
    DialogErrorBar,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIcon,
    MatProgressSpinnerModule,
    FormField,
    FormFieldWrapper,
  ],
  templateUrl: './change-password-dialog.html',
})
export class ChangePasswordDialog {
  readonly dialogRef = inject(MatDialogRef<ChangePasswordDialog>);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);

  readonly submission = dialogSubmission({ clearErrorOn: () => this.model() });
  readonly showCurrentPassword = signal(false);
  readonly showNewPassword = signal(false);
  readonly showConfirmPassword = signal(false);

  model = signal<ChangePasswordModel>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  passwordForm = form(this.model, (schemaPath) => {
    required(schemaPath.currentPassword, { message: 'Zadejte aktuální heslo' });
    required(schemaPath.newPassword, { message: 'Zadejte nové heslo' });
    passwordStrengthRules(schemaPath.newPassword);
    required(schemaPath.confirmPassword, { message: 'Zopakujte nové heslo' });
  });

  private markAllAsTouched(): void {
    this.passwordForm.currentPassword().markAsTouched();
    this.passwordForm.newPassword().markAsTouched();
    this.passwordForm.confirmPassword().markAsTouched();
  }

  async handleSubmit(event: Event): Promise<void> {
    event.preventDefault();
    this.markAllAsTouched();

    if (this.passwordForm().invalid()) {
      this.notificationService.showValidationError('Formulář není správně vyplněn');
      return;
    }

    const { currentPassword, newPassword, confirmPassword } = this.model();
    if (newPassword !== confirmPassword) {
      this.submission.setError('Hesla se neshodují');
      return;
    }

    const outcome = await this.submission.run(
      defer(() => this.authService.changePassword(currentPassword, newPassword)),
      'ChangePasswordDialog.changePassword',
    );
    if (outcome.outcome === 'success') {
      this.notificationService.showSuccess('Heslo bylo úspěšně změněno');
    }
  }
}
